import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../App';

const API_URL = process.env.REACT_APP_API_URL || '/api';

function formatDate(d) {
  return d.toISOString().split('T')[0];
}

function displayDate(isoStr) {
  if (!isoStr) return '';
  const d = new Date(isoStr + 'T00:00:00');
  return d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

function timeAgo(dateStr) {
  if (!dateStr) return '';
  const now = new Date();
  const d = new Date(dateStr);
  const diff = (now - d) / 1000;
  if (diff < 60) return 'gerade eben';
  if (diff < 3600) return `vor ${Math.floor(diff / 60)} Min.`;
  if (diff < 86400) return `vor ${Math.floor(diff / 3600)} Std.`;
  if (diff < 172800) return 'gestern';
  return d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' });
}

// ── Farbsystem ──
const STATUS_COLORS = {
  kritisch: '#c0392b',
  achtung: '#e67e22',
  beobachten: '#f39c12',
  ok: '#27ae60',
};
const STATUS_BG = {
  kritisch: 'rgba(192,57,43,0.08)',
  achtung: 'rgba(230,126,34,0.08)',
  beobachten: 'rgba(243,156,18,0.06)',
  ok: 'rgba(39,174,96,0.06)',
};
const STATUS_LABELS = {
  kritisch: 'Kritisch',
  achtung: 'Achtung',
  beobachten: 'Im Blick',
  ok: 'OK',
};
const STATUS_ICONS = {
  kritisch: '\u26A0\uFE0F',
  achtung: '\u26A1',
  beobachten: '\uD83D\uDC41\uFE0F',
  ok: '\u2705',
};

// ── Postfach-Modal ──
function InboxModal({ email, name, token, onClose, initialFilter = 'all' }) {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [unread, setUnread] = useState(0);
  const [criticalCount, setCriticalCount] = useState(0);
  const [filterMode, setFilterMode] = useState(initialFilter); // 'all' oder 'critical'
  const [sortMode, setSortMode] = useState(initialFilter === 'critical' ? 'priority' : 'date');

  useEffect(() => {
    if (!email || !token) return;
    setLoading(true);
    const url = `${API_URL}/admin/mail-inbox/${encodeURIComponent(email)}?page=${page}&page_size=30&filter_mode=${filterMode}&sort_mode=${sortMode}`;
    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => { if (!r.ok) throw new Error(`${r.status}`); return r.json(); })
      .then(data => {
        setMessages(data.messages || []);
        setTotalPages(data.total_pages);
        setTotal(data.total);
        setUnread(data.unread || 0);
        setCriticalCount(data.critical_count || 0);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [email, token, page, filterMode, sortMode]);

  // Filter wechseln → Seite zurücksetzen
  const switchFilter = (mode) => {
    setFilterMode(mode);
    setSortMode(mode === 'critical' ? 'priority' : 'date');
    setPage(1);
  };

  const card = {
    background: '#fff', border: '1px solid var(--brand-warm-gray)',
    borderRadius: 8, padding: '1.25rem',
  };

  // Prio-Farben für Zeilenhervorhebung
  const rowBg = (mail) => {
    if (mail.priority_level === 'kritisch') return 'rgba(192,57,43,0.06)';
    if (mail.priority_level === 'achtung') return 'rgba(230,126,34,0.05)';
    if (!mail.is_read) return 'rgba(193,167,111,0.04)';
    return 'transparent';
  };
  const rowBorder = (mail) => {
    if (mail.priority_level === 'kritisch') return '3px solid #c0392b';
    if (mail.priority_level === 'achtung') return '3px solid #e67e22';
    return '3px solid transparent';
  };

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(11,29,58,0.5)',
      zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: 16,
    }} onClick={onClose}>
      <div style={{
        background: 'var(--brand-cream)', borderRadius: 12, width: '100%', maxWidth: 760,
        maxHeight: '85dvh', display: 'flex', flexDirection: 'column',
        boxShadow: '0 20px 60px rgba(0,0,0,0.25)',
      }} onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div style={{
          padding: '16px 20px', borderBottom: '1px solid var(--brand-warm-gray)',
          background: '#fff', borderRadius: '12px 12px 0 0',
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div>
              <div style={{ fontSize: 16, fontWeight: 600, color: 'var(--brand-navy)' }}>
                Posteingang — {name}
              </div>
              <div style={{ fontSize: 12, color: 'var(--brand-text-muted)' }}>
                {email} · {total} Mails · {unread} ungelesen
              </div>
            </div>
            <button onClick={onClose} style={{
              width: 32, height: 32, border: 'none', background: 'var(--brand-cream)',
              borderRadius: 6, cursor: 'pointer', fontSize: 18, color: 'var(--brand-navy-70)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>×</button>
          </div>

          {/* Filter-Tabs */}
          <div style={{ display: 'flex', gap: 0, marginTop: 12, borderTop: '1px solid var(--brand-warm-gray)', paddingTop: 10 }}>
            <button onClick={() => switchFilter('all')} style={{
              padding: '8px 14px', fontSize: 12, border: 'none', cursor: 'pointer',
              fontFamily: 'var(--font-body)', borderRadius: '4px 0 0 4px',
              background: filterMode === 'all' ? 'var(--brand-navy)' : 'var(--brand-cream)',
              color: filterMode === 'all' ? '#fff' : 'var(--brand-navy)',
              fontWeight: filterMode === 'all' ? 600 : 400,
            }}>Alle Mails</button>
            <button onClick={() => switchFilter('critical')} style={{
              padding: '8px 14px', fontSize: 12, border: 'none', cursor: 'pointer',
              fontFamily: 'var(--font-body)', borderRadius: '0 4px 4px 0',
              background: filterMode === 'critical' ? '#c0392b' : 'var(--brand-cream)',
              color: filterMode === 'critical' ? '#fff' : 'var(--brand-navy)',
              fontWeight: filterMode === 'critical' ? 600 : 400,
              display: 'flex', alignItems: 'center', gap: 6,
            }}>
              Nur kritische
              {criticalCount > 0 && filterMode !== 'critical' && (
                <span style={{
                  background: '#c0392b', color: '#fff', fontSize: 10, fontWeight: 700,
                  padding: '3px 8px', borderRadius: 8, minWidth: 16, textAlign: 'center',
                }}>{criticalCount}</span>
              )}
            </button>

            {filterMode === 'all' && (
              <button onClick={() => setSortMode(s => s === 'priority' ? 'date' : 'priority')} style={{
                marginLeft: 'auto', padding: '6px 12px', fontSize: 11, border: '1px solid var(--brand-warm-gray)',
                borderRadius: 4, cursor: 'pointer', fontFamily: 'var(--font-body)',
                background: sortMode === 'priority' ? 'rgba(192,57,43,0.08)' : '#fff',
                color: sortMode === 'priority' ? '#c0392b' : 'var(--brand-navy)',
              }}>
                {sortMode === 'priority' ? 'Kritische zuerst' : 'Nach Datum'}
              </button>
            )}
          </div>
        </div>

        {/* Body */}
        <div style={{ flex: 1, overflow: 'auto', padding: '16px 20px' }}>
          {loading ? (
            <div style={{ textAlign: 'center', padding: '3rem', color: 'var(--brand-text-muted)', fontSize: 13 }}>
              {filterMode === 'critical' ? 'Kritische Mails werden analysiert...' : 'Posteingang wird geladen...'}
            </div>
          ) : messages.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '3rem', color: 'var(--brand-text-muted)', fontSize: 13 }}>
              {filterMode === 'critical' ? (
                <>
                  <div style={{ fontSize: 28, marginBottom: 8 }}>{STATUS_ICONS.ok}</div>
                  <div style={{ fontWeight: 500 }}>Keine kritischen Mails</div>
                  <div style={{ fontSize: 12, marginTop: 4 }}>Alles im gruenen Bereich bei {name}.</div>
                </>
              ) : 'Keine E-Mails gefunden.'}
            </div>
          ) : (
            <div style={card}>
              {messages.map((mail, i) => (
                <div key={mail.id} style={{
                  display: 'flex', gap: 10, padding: '10px 8px 10px 4px',
                  borderBottom: i < messages.length - 1 ? '1px solid var(--brand-warm-gray)' : 'none',
                  background: rowBg(mail),
                  borderLeft: rowBorder(mail),
                  borderRadius: mail.priority_level === 'kritisch' || mail.priority_level === 'achtung' ? 4 : 0,
                  marginBottom: mail.priority_level === 'kritisch' || mail.priority_level === 'achtung' ? 4 : 0,
                  transition: 'background 0.15s',
                }}>
                  {/* Priority-Indikator */}
                  <div style={{ width: 8, flexShrink: 0, paddingTop: 6, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
                    {mail.priority_level === 'kritisch' && (
                      <div style={{ width: 8, height: 8, borderRadius: '50%', background: '#c0392b' }} title="Kritisch" />
                    )}
                    {mail.priority_level === 'achtung' && (
                      <div style={{ width: 8, height: 8, borderRadius: '50%', background: '#e67e22' }} title="Achtung" />
                    )}
                    {mail.priority_level === 'ok' && !mail.is_read && (
                      <div style={{ width: 6, height: 6, borderRadius: '50%', background: 'var(--brand-gold)' }} />
                    )}
                    {mail.priority_level === 'beobachten' && (
                      <div style={{ width: 7, height: 7, borderRadius: '50%', background: '#f39c12' }} title="Im Blick" />
                    )}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 2, flexWrap: 'wrap' }}>
                      <span style={{
                        fontSize: 13, fontWeight: mail.is_read ? 400 : 600,
                        color: 'var(--brand-navy)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                      }}>
                        {mail.from_name || mail.from_email}
                      </span>
                      {mail.is_external && (
                        <span style={{
                          fontSize: 9, padding: '1px 5px', borderRadius: 3,
                          background: 'var(--brand-gold-10)', color: 'var(--brand-gold)',
                          fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', flexShrink: 0,
                        }}>extern</span>
                      )}
                      {mail.importance === 'high' && (
                        <span style={{ fontSize: 9, padding: '1px 5px', borderRadius: 3, background: '#fde8e8', color: 'var(--brand-danger)', fontWeight: 600, flexShrink: 0 }}>wichtig</span>
                      )}
                      {mail.flagged && (
                        <span style={{ fontSize: 9, padding: '1px 5px', borderRadius: 3, background: '#fef3e2', color: '#e67e22', fontWeight: 600, flexShrink: 0 }}>nachverfolgen</span>
                      )}
                      {mail.has_attachments && <span style={{ fontSize: 11, color: 'var(--brand-text-muted)', flexShrink: 0 }}>&#128206;</span>}
                    </div>
                    <div style={{
                      fontSize: 13, fontWeight: mail.is_read ? 400 : 500,
                      color: 'var(--brand-navy)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', marginBottom: 2,
                    }}>{mail.subject}</div>
                    <div style={{
                      fontSize: 12, color: 'var(--brand-text-muted)',
                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                    }}>{mail.preview}</div>
                    {/* Priority-Reasons */}
                    {mail.priority_reasons && mail.priority_reasons.length > 0 && (
                      <div style={{ display: 'flex', gap: 4, marginTop: 4, flexWrap: 'wrap' }}>
                        {mail.priority_reasons.map((reason, ri) => (
                          <span key={ri} style={{
                            fontSize: 10, padding: '3px 8px', borderRadius: 3,
                            background: mail.priority_level === 'kritisch' ? 'rgba(192,57,43,0.08)' :
                              mail.priority_level === 'achtung' ? 'rgba(230,126,34,0.08)' : 'var(--brand-cream)',
                            color: mail.priority_level === 'kritisch' ? '#c0392b' :
                              mail.priority_level === 'achtung' ? '#e67e22' : 'var(--brand-text-muted)',
                            fontWeight: 500,
                          }}>
                            {reason}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                  <div style={{ fontSize: 11, color: 'var(--brand-text-muted)', flexShrink: 0, paddingTop: 2, whiteSpace: 'nowrap' }}>
                    {timeAgo(mail.received)}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div style={{
            padding: '12px 20px', borderTop: '1px solid var(--brand-warm-gray)',
            display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 8, background: '#fff',
            borderRadius: '0 0 12px 12px',
          }}>
            <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}
              style={{ padding: '8px 14px', fontSize: 12, border: '1px solid var(--brand-warm-gray)', borderRadius: 4, background: '#fff', color: 'var(--brand-navy)', cursor: page === 1 ? 'default' : 'pointer', opacity: page === 1 ? 0.4 : 1 }}
            >Zurueck</button>
            <span style={{ fontSize: 12, color: 'var(--brand-text-muted)' }}>Seite {page} von {totalPages}</span>
            <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}
              style={{ padding: '8px 14px', fontSize: 12, border: '1px solid var(--brand-warm-gray)', borderRadius: 4, background: '#fff', color: 'var(--brand-navy)', cursor: page === totalPages ? 'default' : 'pointer', opacity: page === totalPages ? 0.4 : 1 }}
            >Weiter</button>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Digest-Einstellungen Modal ──
function DigestSettingsModal({ token, onClose }) {
  const [recipients, setRecipients] = useState('');
  const [enabled, setEnabled] = useState(false);
  const [day, setDay] = useState('monday');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);
  const [sendResult, setSendResult] = useState(null);

  useEffect(() => {
    fetch(`${API_URL}/admin/mail-digest/settings`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => r.ok ? r.json() : {})
      .then(data => {
        setRecipients(data.recipients || '');
        setEnabled(data.enabled || false);
        setDay(data.day || 'monday');
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [token]);

  const handleSave = () => {
    setSaving(true);
    fetch(`${API_URL}/admin/mail-digest/settings`, {
      method: 'PUT',
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ recipients, enabled, day }),
    })
      .then(r => r.json())
      .then(() => { setSaving(false); })
      .catch(() => setSaving(false));
  };

  const handleSendNow = () => {
    setSending(true);
    setSendResult(null);
    const recList = recipients.split(',').map(e => e.trim()).filter(Boolean);
    fetch(`${API_URL}/admin/mail-digest/send`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ recipients: recList }),
    })
      .then(r => r.json())
      .then(data => {
        setSending(false);
        setSendResult(data.status === 'sent' ? 'success' : 'error');
      })
      .catch(() => { setSending(false); setSendResult('error'); });
  };

  const dayLabels = {
    monday: 'Montag', tuesday: 'Dienstag', wednesday: 'Mittwoch',
    thursday: 'Donnerstag', friday: 'Freitag',
  };

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(11,29,58,0.5)',
      zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
    }} onClick={onClose}>
      <div style={{
        background: '#fff', borderRadius: 12, width: '100%', maxWidth: 520,
        boxShadow: '0 20px 60px rgba(0,0,0,0.25)',
      }} onClick={e => e.stopPropagation()}>
        <div style={{
          padding: '20px 24px', borderBottom: '1px solid var(--brand-warm-gray)',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        }}>
          <div>
            <div style={{ fontSize: 16, fontWeight: 600, color: 'var(--brand-navy)' }}>Woechentlicher Digest</div>
            <div style={{ fontSize: 12, color: 'var(--brand-text-muted)' }}>E-Mail-Zusammenfassung fuer die Geschaeftsfuehrung</div>
          </div>
          <button onClick={onClose} style={{
            width: 32, height: 32, border: 'none', background: 'var(--brand-cream)',
            borderRadius: 6, cursor: 'pointer', fontSize: 18, color: 'var(--brand-navy-70)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>×</button>
        </div>

        {loading ? (
          <div style={{ padding: 40, textAlign: 'center', color: 'var(--brand-text-muted)', fontSize: 13 }}>Laden...</div>
        ) : (
          <div style={{ padding: '20px 24px' }}>
            {/* Aktiviert */}
            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }}>
                <div
                  onClick={() => setEnabled(!enabled)}
                  style={{
                    width: 40, height: 22, borderRadius: 11,
                    background: enabled ? 'var(--brand-navy)' : '#ccc',
                    position: 'relative', transition: 'background 0.2s', cursor: 'pointer',
                  }}
                >
                  <div style={{
                    width: 18, height: 18, borderRadius: '50%', background: '#fff',
                    position: 'absolute', top: 2,
                    left: enabled ? 20 : 2,
                    transition: 'left 0.2s',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
                  }} />
                </div>
                <span style={{ fontSize: 13, fontWeight: 500, color: 'var(--brand-navy)' }}>
                  Automatischer woechentlicher Versand
                </span>
              </label>
            </div>

            {/* Wochentag */}
            <div style={{ marginBottom: 16 }}>
              <div style={{ fontSize: 11, color: 'var(--brand-text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 6 }}>Versandtag</div>
              <div style={{ display: 'flex', gap: 4 }}>
                {Object.entries(dayLabels).map(([key, label]) => (
                  <button key={key} onClick={() => setDay(key)} style={{
                    padding: '6px 12px', fontSize: 12, border: '1px solid var(--brand-warm-gray)',
                    borderRadius: 4, cursor: 'pointer', fontFamily: 'var(--font-body)',
                    background: day === key ? 'var(--brand-navy)' : '#fff',
                    color: day === key ? '#fff' : 'var(--brand-navy)',
                    fontWeight: day === key ? 600 : 400,
                  }}>{label}</button>
                ))}
              </div>
            </div>

            {/* Empfaenger */}
            <div style={{ marginBottom: 20 }}>
              <div style={{ fontSize: 11, color: 'var(--brand-text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 6 }}>Empfaenger (kommagetrennt)</div>
              <input
                type="text"
                value={recipients}
                onChange={e => setRecipients(e.target.value)}
                placeholder="gf@fleet-immobilien.de, tl@fleet-immobilien.de"
                style={{
                  width: '100%', padding: '8px 12px', border: '1px solid var(--brand-warm-gray)',
                  borderRadius: 6, fontSize: 13, fontFamily: 'var(--font-body)',
                  color: 'var(--brand-navy)', boxSizing: 'border-box',
                }}
              />
            </div>

            {/* Buttons */}
            <div style={{ display: 'flex', gap: 8, justifyContent: 'space-between' }}>
              <button onClick={handleSendNow} disabled={sending || !recipients.trim()} style={{
                padding: '8px 20px', fontSize: 13, fontWeight: 500, border: '1px solid var(--brand-warm-gray)',
                borderRadius: 6, cursor: sending ? 'wait' : 'pointer', background: '#fff',
                color: 'var(--brand-navy)', fontFamily: 'var(--font-body)',
                opacity: sending || !recipients.trim() ? 0.5 : 1,
              }}>
                {sending ? 'Wird gesendet...' : 'Jetzt senden'}
              </button>

              <div style={{ display: 'flex', gap: 8 }}>
                <a
                  href={`${API_URL}/admin/mail-digest/preview`}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    padding: '8px 16px', fontSize: 13, border: '1px solid var(--brand-warm-gray)',
                    borderRadius: 6, background: '#fff', color: 'var(--brand-navy)',
                    textDecoration: 'none', display: 'flex', alignItems: 'center',
                    fontFamily: 'var(--font-body)',
                  }}
                >
                  Vorschau
                </a>
                <button onClick={handleSave} disabled={saving} style={{
                  padding: '8px 20px', fontSize: 13, fontWeight: 600, border: 'none',
                  borderRadius: 6, cursor: saving ? 'wait' : 'pointer',
                  background: 'var(--brand-navy)', color: '#fff',
                  fontFamily: 'var(--font-body)',
                }}>
                  {saving ? 'Speichern...' : 'Speichern'}
                </button>
              </div>
            </div>

            {sendResult && (
              <div style={{
                marginTop: 12, padding: '8px 12px', borderRadius: 6, fontSize: 12,
                background: sendResult === 'success' ? 'rgba(39,174,96,0.1)' : 'rgba(192,57,43,0.1)',
                color: sendResult === 'success' ? '#27ae60' : '#c0392b',
              }}>
                {sendResult === 'success' ? 'Digest wurde erfolgreich versendet.' : 'Fehler beim Versand. Bitte Empfaenger pruefen.'}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}


// ── Brennpunkte-Uebersicht (teamuebergreifend) ──
function CriticalOverview({ criticalEmails, token, onOpenInbox, onClose }) {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!criticalEmails || criticalEmails.length === 0 || !token) {
      setLoading(false);
      return;
    }
    const emailList = criticalEmails.join(',');
    fetch(`${API_URL}/admin/mail-critical-overview?user_emails=${encodeURIComponent(emailList)}&limit=40`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => r.ok ? r.json() : { messages: [] })
      .then(data => {
        setMessages(data.messages || []);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [criticalEmails, token]);

  // Gruppiere nach owner_email
  const grouped = {};
  messages.forEach(m => {
    const key = m.owner_email || 'unknown';
    if (!grouped[key]) grouped[key] = [];
    grouped[key].push(m);
  });

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(11,29,58,0.5)',
      zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
    }} onClick={onClose}>
      <div style={{
        background: 'var(--brand-cream)', borderRadius: 12, width: '100%', maxWidth: 820,
        maxHeight: '85dvh', display: 'flex', flexDirection: 'column',
        boxShadow: '0 20px 60px rgba(0,0,0,0.25)',
      }} onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div style={{
          padding: '16px 20px', borderBottom: '1px solid var(--brand-warm-gray)',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          background: '#fff', borderRadius: '12px 12px 0 0',
        }}>
          <div>
            <div style={{ fontSize: 16, fontWeight: 600, color: '#c0392b', display: 'flex', alignItems: 'center', gap: 8 }}>
              {STATUS_ICONS.kritisch} Brennpunkte — Alle kritischen Mails
            </div>
            <div style={{ fontSize: 12, color: 'var(--brand-text-muted)' }}>
              {messages.length} kritische Mails bei {Object.keys(grouped).length} Mitarbeiter(n)
            </div>
          </div>
          <button onClick={onClose} style={{
            width: 32, height: 32, border: 'none', background: 'var(--brand-cream)',
            borderRadius: 6, cursor: 'pointer', fontSize: 18, color: 'var(--brand-navy-70)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>×</button>
        </div>

        {/* Body */}
        <div style={{ flex: 1, overflow: 'auto', padding: '16px 20px' }}>
          {loading ? (
            <div style={{ textAlign: 'center', padding: '3rem', color: 'var(--brand-text-muted)', fontSize: 13 }}>
              Kritische Mails werden teamuebergreifend analysiert...
            </div>
          ) : messages.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '3rem' }}>
              <div style={{ fontSize: 32, marginBottom: 8 }}>{STATUS_ICONS.ok}</div>
              <div style={{ fontWeight: 500, color: 'var(--brand-navy)', fontSize: 14 }}>Keine Brennpunkte</div>
              <div style={{ fontSize: 12, color: 'var(--brand-text-muted)', marginTop: 4 }}>Aktuell gibt es keine kritischen unbearbeiteten Mails im Team.</div>
            </div>
          ) : (
            Object.entries(grouped).map(([ownerEmail, mails]) => (
              <div key={ownerEmail} style={{ marginBottom: 16 }}>
                {/* Mitarbeiter-Header */}
                <div style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  padding: '8px 12px', background: '#fff', borderRadius: '8px 8px 0 0',
                  border: '1px solid var(--brand-warm-gray)', borderBottom: 'none',
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <div style={{
                      width: 28, height: 28, borderRadius: '50%', background: 'rgba(192,57,43,0.08)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: 12, fontWeight: 700, color: '#c0392b',
                    }}>{mails.length}</div>
                    <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--brand-navy)' }}>{ownerEmail.split('@')[0].toUpperCase()}</span>
                    <span style={{ fontSize: 11, color: 'var(--brand-text-muted)' }}>{ownerEmail}</span>
                  </div>
                  <button onClick={() => { onClose(); onOpenInbox({ email: ownerEmail, name: ownerEmail.split('@')[0].toUpperCase(), filter: 'critical' }); }}
                    style={{
                      padding: '4px 10px', fontSize: 11, border: '1px solid var(--brand-warm-gray)',
                      borderRadius: 4, background: '#fff', cursor: 'pointer', color: 'var(--brand-navy)',
                      fontFamily: 'var(--font-body)',
                    }}
                  >
                    Postfach oeffnen
                  </button>
                </div>

                {/* Mails dieses Mitarbeiters */}
                <div style={{
                  background: '#fff', border: '1px solid var(--brand-warm-gray)',
                  borderRadius: '0 0 8px 8px', overflow: 'hidden',
                }}>
                  {mails.map((mail, i) => (
                    <div key={mail.id} style={{
                      display: 'flex', gap: 10, padding: '10px 12px',
                      borderBottom: i < mails.length - 1 ? '1px solid var(--brand-warm-gray)' : 'none',
                      background: mail.priority_level === 'kritisch' ? 'rgba(192,57,43,0.04)' : 'rgba(230,126,34,0.03)',
                      borderLeft: `3px solid ${STATUS_COLORS[mail.priority_level] || '#ccc'}`,
                    }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 2, flexWrap: 'wrap' }}>
                          <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--brand-navy)' }}>
                            {mail.from_name || mail.from_email}
                          </span>
                          {mail.is_external && (
                            <span style={{ fontSize: 9, padding: '1px 5px', borderRadius: 3, background: 'var(--brand-gold-10)', color: 'var(--brand-gold)', fontWeight: 600, textTransform: 'uppercase' }}>extern</span>
                          )}
                          {mail.importance === 'high' && (
                            <span style={{ fontSize: 9, padding: '1px 5px', borderRadius: 3, background: '#fde8e8', color: '#c0392b', fontWeight: 600 }}>wichtig</span>
                          )}
                          {mail.flagged && (
                            <span style={{ fontSize: 9, padding: '1px 5px', borderRadius: 3, background: '#fef3e2', color: '#e67e22', fontWeight: 600 }}>nachverfolgen</span>
                          )}
                        </div>
                        <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--brand-navy)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', marginBottom: 2 }}>
                          {mail.subject}
                        </div>
                        <div style={{ fontSize: 12, color: 'var(--brand-text-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', marginBottom: 4 }}>
                          {mail.preview}
                        </div>
                        {mail.priority_reasons && mail.priority_reasons.length > 0 && (
                          <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                            {mail.priority_reasons.map((reason, ri) => (
                              <span key={ri} style={{
                                fontSize: 10, padding: '3px 8px', borderRadius: 3,
                                background: 'rgba(192,57,43,0.08)', color: '#c0392b', fontWeight: 500,
                              }}>{reason}</span>
                            ))}
                          </div>
                        )}
                      </div>
                      <div style={{ fontSize: 11, color: 'var(--brand-text-muted)', flexShrink: 0, whiteSpace: 'nowrap' }}>
                        {timeAgo(mail.received)}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}


// ── Hauptkomponente ──
export default function Statistics() {
  const { token } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  // Ticket #46: Die E-Mail-Statistik ist stillgelegt.
  //
  // Sie las die Fleet-Postfaecher ueber den Fleet-Tenant. Seit dem Ausstieg hat das
  // one.immo-Konto dort keinen Zugriff mehr (AADSTS90072), der Abschnitt zeigte nur
  // noch eine Fehlermeldung. Wiederanbinden haette eine eigene App-Registrierung im
  // Fleet-Tenant plus Zustimmung eines Fleet-Admins gebraucht.
  // Entscheidung Jonas 2026-08-01: nicht reaktivieren, der Fleet-Mailweg laeuft aus.
  // Statt einer Fehlermeldung steht jetzt ein Hinweis. Zum Wiederbeleben die Flagge
  // auf false setzen; der Ladepfad und die Darstellung sind unveraendert erhalten.
  const MAIL_STATISTIK_STILLGELEGT = true;

  const [mailStats, setMailStats] = useState(null);
  const [mailLoading, setMailLoading] = useState(true);
  const [mailError, setMailError] = useState(null);
  const [mailFilter, setMailFilter] = useState('all');

  // Datums-Auswahl
  const today = formatDate(new Date());
  const [dateMode, setDateMode] = useState('today'); // 'today', 'day', 'range'
  const [dateFrom, setDateFrom] = useState(today);
  const [dateTo, setDateTo] = useState(today);

  // Modals
  const [inboxModal, setInboxModal] = useState(null); // { email, name, filter? }
  const [digestModal, setDigestModal] = useState(false);
  const [criticalOverview, setCriticalOverview] = useState(false);

  // Prioritäten-Board: Tab-Filter
  const [prioFilter, setPrioFilter] = useState('alle'); // alle, kritisch, achtung, beobachten

  useEffect(() => {
    if (!token) return;
    Promise.all([
      fetch(`${API_URL}/admin/stats`, { headers: { Authorization: `Bearer ${token}` } })
        .then(r => r.ok ? r.json() : null),
      fetch(`${API_URL}/admin/statistics`, { headers: { Authorization: `Bearer ${token}` } })
        .then(r => r.ok ? r.json() : null),
    ]).then(([s, ext]) => {
      setStats({ ...s, ...ext });
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [token]);

  // Mail-Statistiken bei Filter- oder Datums-Wechsel neu laden
  useEffect(() => {
    if (MAIL_STATISTIK_STILLGELEGT) {
      setMailLoading(false);
      setMailError(null);
      setMailStats(null);
      return;
    }
    if (!token) return;
    setMailLoading(true);
    setMailError(null);

    let url = `${API_URL}/admin/mail-stats?mail_filter=${mailFilter}`;
    if (dateMode === 'day') {
      url += `&date_from=${dateFrom}`;
    } else if (dateMode === 'range') {
      url += `&date_from=${dateFrom}&date_to=${dateTo}`;
    }

    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => {
        if (!r.ok) throw new Error(`Fehler ${r.status}`);
        return r.json();
      })
      .then(data => {
        setMailStats(data);
        setMailLoading(false);
      })
      .catch(err => {
        setMailError(err.message);
        setMailLoading(false);
      });
  }, [token, mailFilter, dateMode, dateFrom, dateTo]);

  if (loading) return (
    <div style={{ padding: 60, textAlign: 'center', color: 'var(--brand-text-muted)' }}>Statistiken werden geladen...</div>
  );

  const card = {
    background: '#fff', border: '1px solid var(--brand-warm-gray)',
    borderRadius: 8, padding: '1.25rem', marginBottom: 16,
  };
  const sectionTitle = {
    fontSize: 13, fontWeight: 600, color: 'var(--brand-navy)',
    textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 12,
  };

  // Dynamische Labels basierend auf Datums-Modus
  const isCustomDate = dateMode !== 'today';
  const periodLabel = dateMode === 'day'
    ? displayDate(dateFrom)
    : dateMode === 'range'
      ? `${displayDate(dateFrom)} — ${displayDate(dateTo)}`
      : 'heute';
  const periodLabelShort = dateMode === 'today' ? 'heute' : 'Zeitraum';
  const secondaryLabel = dateMode === 'today' ? 'in 7 Tagen' : 'gleicher Zeitraum';

  // Quick-Presets
  const setPreset = (preset) => {
    const now = new Date();
    if (preset === 'today') {
      setDateMode('today');
    } else if (preset === 'yesterday') {
      const y = new Date(now);
      y.setDate(y.getDate() - 1);
      setDateFrom(formatDate(y));
      setDateMode('day');
    } else if (preset === 'week') {
      const from = new Date(now);
      from.setDate(from.getDate() - 7);
      setDateFrom(formatDate(from));
      setDateTo(formatDate(now));
      setDateMode('range');
    } else if (preset === 'month') {
      const from = new Date(now);
      from.setDate(from.getDate() - 30);
      setDateFrom(formatDate(from));
      setDateTo(formatDate(now));
      setDateMode('range');
    }
  };

  // Prioritäts-Filter
  const priorityUsers = mailStats?.users?.filter(u => {
    if (prioFilter === 'alle') return u.priority_level !== 'ok';
    return u.priority_level === prioFilter;
  }).sort((a, b) => (b.priority_score || 0) - (a.priority_score || 0)) || [];

  const summary = mailStats?.summary || {};

  return (
    <div>
      <h1 style={{
        fontFamily: 'var(--font-headline)', fontSize: 24, fontWeight: 400,
        color: 'var(--brand-navy)', marginBottom: 4,
      }}>Statistiken</h1>
      <p style={{ fontSize: 13, color: 'var(--brand-text-muted)', marginBottom: 24 }}>
        Nutzungsstatistiken und Plattform-Metriken
      </p>

      {/* Uebersichts-Karten */}
      <div className="stats-grid-4" style={{ marginBottom: 24 }}>
        <div style={card}>
          <div style={{ fontSize: 11, color: 'var(--brand-text-muted)', textTransform: 'uppercase', letterSpacing: '0.8px', marginBottom: 6 }}>Benutzer</div>
          <div style={{ fontFamily: 'var(--font-headline)', fontSize: 32, color: 'var(--brand-navy)' }}>{stats?.active_users ?? '—'}</div>
          <div style={{ fontSize: 12, color: 'var(--brand-text-muted)', marginTop: 4 }}>von {stats?.total_users ?? '—'} gesamt</div>
        </div>
        <div style={card}>
          <div style={{ fontSize: 11, color: 'var(--brand-text-muted)', textTransform: 'uppercase', letterSpacing: '0.8px', marginBottom: 6 }}>Skill-Ausfuehrungen</div>
          <div style={{ fontFamily: 'var(--font-headline)', fontSize: 32, color: 'var(--brand-navy)' }}>{stats?.total_executions ?? '—'}</div>
          <div style={{ fontSize: 12, color: 'var(--brand-text-muted)', marginTop: 4 }}>gesamt</div>
        </div>
        <div style={card}>
          <div style={{ fontSize: 11, color: 'var(--brand-text-muted)', textTransform: 'uppercase', letterSpacing: '0.8px', marginBottom: 6 }}>Ausfuehrungen (7 Tage)</div>
          <div style={{ fontFamily: 'var(--font-headline)', fontSize: 32, color: 'var(--brand-navy)' }}>{stats?.executions_7d ?? '—'}</div>
          <div style={{ fontSize: 12, color: 'var(--brand-text-muted)', marginTop: 4 }}>letzte Woche</div>
        </div>
        <div style={card}>
          <div style={{ fontSize: 11, color: 'var(--brand-text-muted)', textTransform: 'uppercase', letterSpacing: '0.8px', marginBottom: 6 }}>Fehlerrate</div>
          <div style={{ fontFamily: 'var(--font-headline)', fontSize: 32, color: stats?.error_rate > 10 ? 'var(--brand-danger)' : 'var(--brand-success)' }}>
            {stats?.error_rate != null ? `${stats.error_rate}%` : '—'}
          </div>
          <div style={{ fontSize: 12, color: 'var(--brand-text-muted)', marginTop: 4 }}>letzte 30 Tage</div>
        </div>
      </div>

      <div className="stats-grid-2">
        {/* Nutzung pro Skill */}
        <div style={card}>
          <div style={sectionTitle}>Nutzung pro Skill</div>
          {stats?.skill_usage ? (
            stats.skill_usage.map(s => (
              <div key={s.skill_id} style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
                <div style={{ flex: 1, fontSize: 13, color: 'var(--brand-navy)' }}>{s.skill_id}</div>
                <div style={{ width: 120, height: 6, background: 'var(--brand-cream)', borderRadius: 3, overflow: 'hidden' }}>
                  <div style={{
                    width: `${Math.min(100, (s.count / Math.max(1, stats.total_executions)) * 100 * 3)}%`,
                    height: '100%', background: 'var(--brand-gold)', borderRadius: 3,
                  }} />
                </div>
                <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--brand-navy)', minWidth: 30, textAlign: 'right' }}>{s.count}</div>
              </div>
            ))
          ) : (
            <div style={{ color: 'var(--brand-text-muted)', fontSize: 13 }}>Keine Daten</div>
          )}
        </div>

        {/* Aktivste Nutzer */}
        <div style={card}>
          <div style={sectionTitle}>Aktivste Nutzer (30 Tage)</div>
          {stats?.top_users ? (
            stats.top_users.map((u, i) => (
              <div key={u.name} style={{
                display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8,
                padding: '6px 0', borderBottom: i < stats.top_users.length - 1 ? '1px solid var(--brand-warm-gray)' : 'none',
              }}>
                <div style={{
                  width: 24, height: 24, borderRadius: '50%', background: 'var(--brand-gold-10)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 11, fontWeight: 700, color: 'var(--brand-gold)', flexShrink: 0,
                }}>{i + 1}</div>
                <div style={{ flex: 1, fontSize: 13, color: 'var(--brand-navy)' }}>{u.name}</div>
                <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--brand-text-muted)' }}>{u.count} Vorgaenge</div>
              </div>
            ))
          ) : (
            <div style={{ color: 'var(--brand-text-muted)', fontSize: 13 }}>Keine Daten</div>
          )}
        </div>
      </div>

      {/* ═══════════════════════════════════════════════════════════ */}
      {/* GF E-Mail-Cockpit                                        */}
      {/* ═══════════════════════════════════════════════════════════ */}
      <div style={{ marginTop: 32 }}>
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16, flexWrap: 'wrap', gap: 12 }}>
          <div>
            <h2 style={{
              fontFamily: 'var(--font-headline)', fontSize: 20, fontWeight: 400,
              color: 'var(--brand-navy)', margin: 0,
            }}>E-Mail-Cockpit</h2>
            <p style={{ fontSize: 12, color: 'var(--brand-text-muted)', margin: '2px 0 0' }}>
              Ueberblick, Prioritaeten & Handlungsbedarf
            </p>
          </div>

          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            {/* Digest Button */}
            <button onClick={() => setDigestModal(true)} style={{
              padding: '7px 14px', fontSize: 12, fontWeight: 500,
              border: '1px solid var(--brand-warm-gray)', borderRadius: 6,
              background: '#fff', color: 'var(--brand-navy)', cursor: 'pointer',
              fontFamily: 'var(--font-body)', display: 'flex', alignItems: 'center', gap: 6,
            }}>
              <span style={{ fontSize: 14 }}>&#9993;</span> Woechentlicher Digest
            </button>

            {/* Mail-Filter */}
            <div className="filter-bar">
              {[
                { key: 'all', label: 'Alle' },
                { key: 'external', label: 'Nur extern' },
                { key: 'internal', label: 'Nur intern' },
              ].map(f => (
                <button
                  key={f.key}
                  onClick={() => setMailFilter(f.key)}
                  disabled={mailLoading}
                  style={{
                    padding: '5px 14px', fontSize: 12,
                    fontWeight: mailFilter === f.key ? 600 : 400,
                    color: mailFilter === f.key ? '#fff' : 'var(--brand-navy)',
                    background: mailFilter === f.key ? 'var(--brand-navy, #1a2744)' : 'transparent',
                    border: 'none', borderRadius: 4,
                    cursor: mailLoading ? 'wait' : 'pointer',
                    transition: 'all 0.15s',
                    opacity: mailLoading ? 0.6 : 1,
                    fontFamily: 'var(--font-body)',
                  }}
                >{f.label}</button>
              ))}
            </div>
          </div>
        </div>

        {/* Datums-Auswahl */}
        <div style={{
          ...card, marginBottom: 16, padding: '12px 16px',
          display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap',
        }}>
          <div style={{ display: 'flex', gap: 4 }}>
            {[
              { key: 'today', label: 'Heute' },
              { key: 'yesterday', label: 'Gestern' },
              { key: 'week', label: '7 Tage' },
              { key: 'month', label: '30 Tage' },
            ].map(p => (
              <button
                key={p.key}
                onClick={() => setPreset(p.key)}
                disabled={mailLoading}
                style={{
                  padding: '5px 12px', fontSize: 12, fontWeight: 500,
                  border: '1px solid var(--brand-warm-gray)', borderRadius: 4,
                  background: (dateMode === 'today' && p.key === 'today') ? 'var(--brand-navy)' :
                    (dateMode === 'day' && p.key === 'yesterday' && dateFrom === formatDate((() => { const d = new Date(); d.setDate(d.getDate() - 1); return d; })())) ? 'var(--brand-navy)' :
                    '#fff',
                  color: (dateMode === 'today' && p.key === 'today') ? '#fff' :
                    (dateMode === 'day' && p.key === 'yesterday' && dateFrom === formatDate((() => { const d = new Date(); d.setDate(d.getDate() - 1); return d; })())) ? '#fff' :
                    'var(--brand-navy)',
                  cursor: mailLoading ? 'wait' : 'pointer',
                  transition: 'all 0.15s',
                  fontFamily: 'var(--font-body)',
                }}
              >{p.label}</button>
            ))}
          </div>

          <div style={{ width: 1, height: 24, background: 'var(--brand-warm-gray)' }} />

          <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12 }}>
            <span style={{ color: 'var(--brand-text-muted)', fontWeight: 500 }}>Von:</span>
            <input type="date" value={dateFrom} max={today}
              onChange={e => { setDateFrom(e.target.value); if (dateMode === 'today') setDateMode('day'); }}
              style={{ padding: '4px 8px', border: '1px solid var(--brand-warm-gray)', borderRadius: 4, fontSize: 12, fontFamily: 'var(--font-body)', color: 'var(--brand-navy)' }}
            />
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12 }}>
            <span style={{ color: 'var(--brand-text-muted)', fontWeight: 500 }}>Bis:</span>
            <input type="date" value={dateTo} min={dateFrom} max={today}
              onChange={e => { setDateTo(e.target.value); setDateMode('range'); }}
              style={{ padding: '4px 8px', border: '1px solid var(--brand-warm-gray)', borderRadius: 4, fontSize: 12, fontFamily: 'var(--font-body)', color: 'var(--brand-navy)' }}
            />
          </div>

          {isCustomDate && (
            <div style={{
              padding: '3px 10px', borderRadius: 4, fontSize: 11, fontWeight: 600,
              background: 'var(--brand-gold-10)', color: 'var(--brand-gold)',
            }}>{periodLabel}</div>
          )}
        </div>

        {mailFilter !== 'all' && (
          <div style={{ fontSize: 12, color: 'var(--brand-text-muted)', marginBottom: 10, fontStyle: 'italic' }}>
            {mailFilter === 'external'
              ? 'Zeigt nur Mails an/von externen Adressen (ausserhalb @fleet-immobilien.de). Posteingang & Ungelesen sind immer gesamt.'
              : 'Zeigt nur interne Mails (@fleet-immobilien.de). Posteingang & Ungelesen sind immer gesamt.'}
          </div>
        )}

        {MAIL_STATISTIK_STILLGELEGT ? (
          <div style={{ ...card, padding: '2rem', fontSize: 13, lineHeight: 1.6 }}>
            <div style={{ fontWeight: 700, marginBottom: 8 }}>Die E-Mail-Statistik wurde eingestellt.</div>
            <div style={{ color: 'var(--brand-text-muted)' }}>
              Sie hat die Postfächer über den Fleet-Mandanten ausgelesen. Dieser Zugang besteht
              nicht mehr, deshalb konnte der Abschnitt zuletzt nur noch eine Fehlermeldung
              anzeigen. Er wird nicht wieder aktiviert. Alle übrigen Auswertungen auf dieser
              Seite sind davon nicht betroffen.
            </div>
          </div>
        ) : mailLoading ? (
          <div style={{ ...card, padding: '3rem', textAlign: 'center', color: 'var(--brand-text-muted)', fontSize: 13 }}>
            <div style={{ marginBottom: 8 }}>Mail-Statistiken werden geladen...</div>
            <div style={{ fontSize: 11 }}>Dies kann bei vielen Postfaechern einen Moment dauern.</div>
          </div>
        ) : mailError ? (
          <div style={{ ...card, padding: '1rem', color: 'var(--brand-danger)', fontSize: 13 }}>
            Fehler beim Laden der Mail-Statistiken: {mailError}
          </div>
        ) : mailStats?.users?.length > 0 ? (
          <>
            {/* ── Gesamt-Status-Banner ── */}
            <div style={{
              background: STATUS_BG[summary.overall_status] || STATUS_BG.ok,
              border: `1px solid ${STATUS_COLORS[summary.overall_status] || STATUS_COLORS.ok}`,
              borderLeft: `4px solid ${STATUS_COLORS[summary.overall_status] || STATUS_COLORS.ok}`,
              borderRadius: 8, padding: '14px 20px', marginBottom: 16,
              display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12,
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <span style={{ fontSize: 20 }}>{STATUS_ICONS[summary.overall_status] || STATUS_ICONS.ok}</span>
                <div>
                  <div style={{ fontSize: 14, fontWeight: 600, color: STATUS_COLORS[summary.overall_status] || STATUS_COLORS.ok }}>
                    Gesamtstatus: {STATUS_LABELS[summary.overall_status] || 'OK'}
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--brand-text-muted)', marginTop: 2 }}>
                    {summary.critical_count || 0} kritisch · {summary.warning_count || 0} Achtung · {summary.watch_count || 0} im Blick · {summary.ok_count || 0} OK
                  </div>
                </div>
              </div>
              <div style={{
                padding: '4px 14px', borderRadius: 20, fontSize: 12, fontWeight: 600,
                background: STATUS_COLORS[summary.overall_status] || STATUS_COLORS.ok,
                color: '#fff',
              }}>
                {summary.response_ratio ?? 0}% Antwort-Quote
              </div>
            </div>

            {/* ── KPI-Karten ── */}
            <div className="stats-grid-4" style={{ marginBottom: 16 }}>
              <div style={card}>
                <div style={{ fontSize: 11, color: 'var(--brand-text-muted)', textTransform: 'uppercase', letterSpacing: '0.8px', marginBottom: 6 }}>Mails im Posteingang</div>
                <div style={{ fontFamily: 'var(--font-headline)', fontSize: 32, color: 'var(--brand-navy)' }}>{summary.total_inbox ?? '—'}</div>
                <div style={{ fontSize: 12, color: 'var(--brand-text-muted)', marginTop: 4 }}>
                  davon <span style={{ fontWeight: 600, color: (summary.total_unread || 0) > 50 ? 'var(--brand-danger)' : 'var(--brand-text-muted)' }}>{summary.total_unread ?? 0}</span> ungelesen
                </div>
              </div>
              <div style={card}>
                <div style={{ fontSize: 11, color: 'var(--brand-text-muted)', textTransform: 'uppercase', letterSpacing: '0.8px', marginBottom: 6 }}>
                  Empfangen {periodLabelShort}
                </div>
                <div style={{ fontFamily: 'var(--font-headline)', fontSize: 32, color: 'var(--brand-navy)' }}>{summary.total_received_today ?? '—'}</div>
                <div style={{ fontSize: 12, color: 'var(--brand-text-muted)', marginTop: 4 }}>
                  {isCustomDate ? periodLabel : `${summary.total_received_7d ?? 0} in 7 Tagen`}
                </div>
              </div>
              <div style={card}>
                <div style={{ fontSize: 11, color: 'var(--brand-text-muted)', textTransform: 'uppercase', letterSpacing: '0.8px', marginBottom: 6 }}>
                  Gesendet {periodLabelShort}
                </div>
                <div style={{ fontFamily: 'var(--font-headline)', fontSize: 32, color: 'var(--brand-navy)' }}>{summary.total_sent_today ?? '—'}</div>
                <div style={{ fontSize: 12, color: 'var(--brand-text-muted)', marginTop: 4 }}>
                  {isCustomDate ? periodLabel : `${summary.total_sent_7d ?? 0} in 7 Tagen`}
                </div>
              </div>
              <div style={card}>
                <div style={{ fontSize: 11, color: 'var(--brand-text-muted)', textTransform: 'uppercase', letterSpacing: '0.8px', marginBottom: 6 }}>Entwuerfe</div>
                <div style={{ fontFamily: 'var(--font-headline)', fontSize: 32, color: (summary.total_drafts || 0) > 20 ? 'var(--brand-danger)' : 'var(--brand-navy)' }}>{summary.total_drafts ?? '—'}</div>
                <div style={{ fontSize: 12, color: 'var(--brand-text-muted)', marginTop: 4 }}>{mailStats.total_users} Mitarbeiter</div>
              </div>
            </div>

            {/* ── Highlight-Karten ── */}
            {summary && (
              <div className="stats-grid-3" style={{ marginBottom: 24 }}>
                {summary.top_sender_today && (
                  <div style={{ ...card, borderLeft: '3px solid var(--brand-gold)', marginBottom: 0 }}>
                    <div style={{ fontSize: 11, color: 'var(--brand-text-muted)', textTransform: 'uppercase', marginBottom: 4 }}>
                      Fleissigster Versender {periodLabelShort}
                    </div>
                    <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--brand-navy)' }}>{summary.top_sender_today.name}</div>
                    <div style={{ fontSize: 12, color: 'var(--brand-text-muted)' }}>{summary.top_sender_today.count} Mails gesendet</div>
                  </div>
                )}
                {summary.top_inbox && (
                  <div style={{ ...card, borderLeft: '3px solid var(--brand-danger, #c0392b)', marginBottom: 0 }}>
                    <div style={{ fontSize: 11, color: 'var(--brand-text-muted)', textTransform: 'uppercase', marginBottom: 4 }}>Vollster Posteingang</div>
                    <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--brand-navy)' }}>{summary.top_inbox.name}</div>
                    <div style={{ fontSize: 12, color: 'var(--brand-text-muted)' }}>{summary.top_inbox.count} Mails</div>
                  </div>
                )}
                {summary.top_unread && (
                  <div style={{ ...card, borderLeft: '3px solid var(--brand-danger, #c0392b)', marginBottom: 0 }}>
                    <div style={{ fontSize: 11, color: 'var(--brand-text-muted)', textTransform: 'uppercase', marginBottom: 4 }}>Meiste ungelesene Mails</div>
                    <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--brand-navy)' }}>{summary.top_unread.name}</div>
                    <div style={{ fontSize: 12, color: 'var(--brand-text-muted)' }}>{summary.top_unread.count} ungelesen</div>
                  </div>
                )}
              </div>
            )}

            {/* ═══════════════════════════════════════════════════════ */}
            {/* Prioritaeten-Board                                     */}
            {/* ═══════════════════════════════════════════════════════ */}
            <div style={{ ...card, marginBottom: 16, padding: 0, overflow: 'hidden' }}>
              {/* Board Header */}
              <div style={{
                padding: '16px 20px 0', background: '#fff',
                borderBottom: '1px solid var(--brand-warm-gray)',
              }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
                  <div style={sectionTitle}>Prioritaeten-Board — Wo muss eingegriffen werden?</div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <div style={{ fontSize: 11, color: 'var(--brand-text-muted)' }}>
                      {priorityUsers.length} {priorityUsers.length === 1 ? 'Eintrag' : 'Eintraege'}
                    </div>
                    {priorityUsers.length > 0 && (
                      <button onClick={() => setCriticalOverview(true)} style={{
                        padding: '5px 12px', fontSize: 11, fontWeight: 600,
                        border: '1px solid #c0392b', borderRadius: 4,
                        background: 'rgba(192,57,43,0.06)', color: '#c0392b',
                        cursor: 'pointer', fontFamily: 'var(--font-body)',
                        display: 'flex', alignItems: 'center', gap: 5,
                      }}>
                        {STATUS_ICONS.kritisch} Alle kritischen Mails anzeigen
                      </button>
                    )}
                  </div>
                </div>
                {/* Filter-Tabs */}
                <div style={{ display: 'flex', gap: 0, marginBottom: -1 }}>
                  {[
                    { key: 'alle', label: 'Alle', count: (mailStats?.users || []).filter(u => u.priority_level !== 'ok').length },
                    { key: 'kritisch', label: 'Kritisch', count: summary.critical_count || 0, color: STATUS_COLORS.kritisch },
                    { key: 'achtung', label: 'Achtung', count: summary.warning_count || 0, color: STATUS_COLORS.achtung },
                    { key: 'beobachten', label: 'Im Blick', count: summary.watch_count || 0, color: STATUS_COLORS.beobachten },
                  ].map(tab => (
                    <button key={tab.key} onClick={() => setPrioFilter(tab.key)} style={{
                      padding: '8px 16px', fontSize: 12, fontWeight: prioFilter === tab.key ? 600 : 400,
                      border: 'none', borderBottom: prioFilter === tab.key ? `2px solid ${tab.color || 'var(--brand-navy)'}` : '2px solid transparent',
                      background: 'transparent', cursor: 'pointer',
                      color: prioFilter === tab.key ? (tab.color || 'var(--brand-navy)') : 'var(--brand-text-muted)',
                      fontFamily: 'var(--font-body)', transition: 'all 0.15s',
                    }}>
                      {tab.label}
                      {tab.count > 0 && (
                        <span style={{
                          marginLeft: 6, fontSize: 10, fontWeight: 700,
                          padding: '3px 8px', borderRadius: 10,
                          background: tab.color ? `${tab.color}15` : 'var(--brand-cream)',
                          color: tab.color || 'var(--brand-navy)',
                        }}>{tab.count}</span>
                      )}
                    </button>
                  ))}
                </div>
              </div>

              {/* Board Body */}
              <div style={{ padding: '16px 20px', background: 'var(--brand-cream, #fafaf6)', minHeight: 80 }}>
                {priorityUsers.length === 0 ? (
                  <div style={{
                    textAlign: 'center', padding: '24px 16px',
                    color: 'var(--brand-text-muted)', fontSize: 13,
                  }}>
                    <div style={{ fontSize: 28, marginBottom: 8 }}>{STATUS_ICONS.ok}</div>
                    <div style={{ fontWeight: 500 }}>Alles im gruenen Bereich</div>
                    <div style={{ fontSize: 12, marginTop: 4 }}>Kein Mitarbeiter benoetigt aktuell Unterstuetzung.</div>
                  </div>
                ) : (
                  <div style={{ display: 'grid', gap: 8 }}>
                    {priorityUsers.map(u => (
                      <div key={u.email} style={{
                        background: '#fff', borderRadius: 8,
                        border: `1px solid ${STATUS_COLORS[u.priority_level]}20`,
                        borderLeft: `4px solid ${STATUS_COLORS[u.priority_level]}`,
                        padding: '12px 16px',
                        display: 'flex', alignItems: 'flex-start', gap: 12,
                        transition: 'box-shadow 0.15s',
                      }}
                        onMouseEnter={e => e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.06)'}
                        onMouseLeave={e => e.currentTarget.style.boxShadow = 'none'}
                      >
                        {/* Status-Indikator */}
                        <div style={{
                          width: 36, height: 36, borderRadius: '50%',
                          background: STATUS_BG[u.priority_level],
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          flexShrink: 0, fontSize: 16,
                        }}>
                          {STATUS_ICONS[u.priority_level]}
                        </div>

                        {/* Inhalt */}
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                            <button
                              onClick={() => setInboxModal({ email: u.email, name: u.name })}
                              style={{
                                background: 'none', border: 'none', padding: 0, cursor: 'pointer',
                                fontSize: 14, fontWeight: 600, color: 'var(--brand-navy)',
                                textDecoration: 'underline', textDecorationColor: 'var(--brand-gold)',
                                textUnderlineOffset: 2, fontFamily: 'var(--font-body)',
                              }}
                            >
                              {u.name}
                            </button>
                            <span style={{
                              fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 10,
                              background: STATUS_BG[u.priority_level],
                              color: STATUS_COLORS[u.priority_level],
                              textTransform: 'uppercase', letterSpacing: '0.5px',
                            }}>
                              {STATUS_LABELS[u.priority_level]}
                            </span>
                            <span style={{ fontSize: 11, color: 'var(--brand-text-muted)' }}>
                              Score: {u.priority_score}
                            </span>
                          </div>

                          {/* Alerts */}
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginBottom: 6 }}>
                            {(u.priority_alerts || []).map((alert, i) => (
                              <span key={i} style={{
                                fontSize: 11, padding: '2px 8px', borderRadius: 4,
                                background: 'var(--brand-cream)', color: 'var(--brand-navy)',
                                border: '1px solid var(--brand-warm-gray)',
                              }}>
                                {alert}
                              </span>
                            ))}
                          </div>

                          {/* Quick Stats */}
                          <div style={{ display: 'flex', gap: 16, fontSize: 11, color: 'var(--brand-text-muted)', alignItems: 'center', flexWrap: 'wrap' }}>
                            <span>Posteingang: <strong style={{ color: 'var(--brand-navy)' }}>{u.inbox_count}</strong></span>
                            <span>Ungelesen: <strong style={{ color: (u.inbox_unread || 0) > 30 ? 'var(--brand-danger)' : 'var(--brand-navy)' }}>{u.inbox_unread}</strong></span>
                            <span>Empf.: <strong style={{ color: 'var(--brand-navy)' }}>{u.received_today ?? 0}</strong></span>
                            <span>Ges.: <strong style={{ color: 'var(--brand-navy)' }}>{u.sent_today ?? 0}</strong></span>
                            {u.oldest_unread && <span>Aelteste: <strong style={{ color: 'var(--brand-danger)' }}>{u.oldest_unread}</strong></span>}
                            <button
                              onClick={() => setInboxModal({ email: u.email, name: u.name, filter: 'critical' })}
                              style={{
                                marginLeft: 'auto', padding: '3px 10px', fontSize: 10, fontWeight: 600,
                                border: `1px solid ${STATUS_COLORS[u.priority_level]}`,
                                borderRadius: 4, cursor: 'pointer', fontFamily: 'var(--font-body)',
                                background: `${STATUS_COLORS[u.priority_level]}10`,
                                color: STATUS_COLORS[u.priority_level],
                              }}
                            >
                              Kritische Mails anzeigen
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* ═══════════════════════════════════════════════════════ */}
            {/* Antwort-Verhalten (Ratio-Balken)                       */}
            {/* ═══════════════════════════════════════════════════════ */}
            <div style={{ ...card, marginBottom: 16 }}>
              <div style={sectionTitle}>Antwortverhalten Team</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
                <div style={{ flex: 1, minWidth: 200 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: 'var(--brand-text-muted)', marginBottom: 4 }}>
                    <span>Antwort-Quote (Gesendet / Empfangen)</span>
                    <span style={{ fontWeight: 600, color: 'var(--brand-navy)' }}>{summary.response_ratio ?? 0}%</span>
                  </div>
                  <div style={{ height: 8, background: 'var(--brand-cream)', borderRadius: 4, overflow: 'hidden' }}>
                    <div style={{
                      width: `${Math.min(100, summary.response_ratio || 0)}%`,
                      height: '100%', borderRadius: 4,
                      background: (summary.response_ratio || 0) >= 60 ? '#27ae60' :
                        (summary.response_ratio || 0) >= 30 ? '#e67e22' : '#c0392b',
                      transition: 'width 0.5s ease',
                    }} />
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10, color: 'var(--brand-text-muted)', marginTop: 4 }}>
                    <span>0%</span>
                    <span style={{ color: '#27ae60' }}>Ziel: 60%+</span>
                    <span>100%</span>
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 20 }}>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: 20, fontWeight: 300, color: 'var(--brand-navy)', fontFamily: 'var(--font-headline)' }}>{summary.total_received_today ?? 0}</div>
                    <div style={{ fontSize: 10, color: 'var(--brand-text-muted)', textTransform: 'uppercase' }}>Empfangen</div>
                  </div>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: 20, fontWeight: 300, color: 'var(--brand-navy)', fontFamily: 'var(--font-headline)' }}>{summary.total_sent_today ?? 0}</div>
                    <div style={{ fontSize: 10, color: 'var(--brand-text-muted)', textTransform: 'uppercase' }}>Gesendet</div>
                  </div>
                </div>
              </div>
            </div>

            {/* ═══════════════════════════════════════════════════════ */}
            {/* Detail-Tabelle                                         */}
            {/* ═══════════════════════════════════════════════════════ */}
            <div style={{ ...card, marginBottom: 0 }}>
              <div style={sectionTitle}>Detail pro Mitarbeiter</div>
              <div className="responsive-table-wrapper" style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                  <thead>
                    <tr style={{ borderBottom: '2px solid var(--brand-warm-gray)' }}>
                      {[
                        'Status', 'Mitarbeiter', 'Posteingang', 'Ungelesen', 'Aelteste ungel.',
                        isCustomDate ? `Empf. ${periodLabelShort}` : 'Empf. heute',
                        isCustomDate ? '' : 'Empf. 7d',
                        isCustomDate ? `Ges. ${periodLabelShort}` : 'Ges. heute',
                        isCustomDate ? '' : 'Ges. 7d',
                        'Entwuerfe',
                      ].filter(Boolean).map((h, idx) => (
                        <th key={h + idx} style={{
                          textAlign: idx <= 1 ? 'left' : 'right',
                          padding: '8px 8px', color: 'var(--brand-navy)', fontWeight: 600, fontSize: 11, whiteSpace: 'nowrap',
                          ...(idx === 0 ? { width: 50, textAlign: 'center' } : {}),
                        }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {mailStats.users.map((u, i) => (
                      <tr key={u.email} style={{
                        borderBottom: '1px solid var(--brand-warm-gray)',
                        background: i % 2 === 0 ? '#fff' : 'var(--brand-cream, #fafaf6)',
                      }}>
                        {/* Status-Dot */}
                        <td style={{ padding: '7px 8px', textAlign: 'center' }}>
                          <div title={`${STATUS_LABELS[u.priority_level]} (Score: ${u.priority_score})`} style={{
                            width: 10, height: 10, borderRadius: '50%', margin: '0 auto',
                            background: STATUS_COLORS[u.priority_level] || STATUS_COLORS.ok,
                          }} />
                        </td>
                        <td style={{ padding: '7px 8px' }}>
                          <button
                            onClick={() => setInboxModal({ email: u.email, name: u.name })}
                            style={{
                              background: 'none', border: 'none', padding: 0, cursor: 'pointer',
                              textAlign: 'left', fontFamily: 'var(--font-body)',
                            }}
                            title={`Posteingang von ${u.name} anzeigen`}
                          >
                            <div style={{
                              fontWeight: 500, color: 'var(--brand-navy)',
                              textDecoration: 'underline', textDecorationColor: 'var(--brand-gold)',
                              textUnderlineOffset: 2,
                            }}>{u.name}</div>
                            <div style={{ fontSize: 11, color: 'var(--brand-text-muted)' }}>{u.email}</div>
                          </button>
                        </td>
                        <td style={{ textAlign: 'right', padding: '7px 8px', fontWeight: 600, color: (u.inbox_count || 0) > 100 ? 'var(--brand-danger)' : 'var(--brand-navy)' }}>
                          {u.inbox_count ?? '—'}
                        </td>
                        <td style={{ textAlign: 'right', padding: '7px 8px', fontWeight: (u.inbox_unread || 0) > 20 ? 600 : 400, color: (u.inbox_unread || 0) > 50 ? 'var(--brand-danger)' : (u.inbox_unread || 0) > 20 ? '#e67e22' : 'var(--brand-text-muted)' }}>
                          {u.inbox_unread ?? '—'}
                        </td>
                        <td style={{ textAlign: 'right', padding: '7px 8px', fontSize: 12, color: u.oldest_unread && parseInt(u.oldest_unread) > 7 ? 'var(--brand-danger)' : 'var(--brand-text-muted)' }}>
                          {u.oldest_unread || '—'}
                        </td>
                        <td style={{ textAlign: 'right', padding: '7px 8px', color: 'var(--brand-navy)' }}>
                          {u.received_today ?? '—'}
                        </td>
                        {!isCustomDate && (
                          <td style={{ textAlign: 'right', padding: '7px 8px', color: 'var(--brand-text-muted)' }}>
                            {u.received_7d ?? '—'}
                          </td>
                        )}
                        <td style={{ textAlign: 'right', padding: '7px 8px', fontWeight: 500, color: 'var(--brand-navy)' }}>
                          {u.sent_today ?? '—'}
                        </td>
                        {!isCustomDate && (
                          <td style={{ textAlign: 'right', padding: '7px 8px', color: 'var(--brand-text-muted)' }}>
                            {u.sent_7d ?? '—'}
                          </td>
                        )}
                        <td style={{ textAlign: 'right', padding: '7px 8px', color: (u.drafts || 0) > 10 ? '#e67e22' : 'var(--brand-text-muted)' }}>
                          {u.drafts ?? '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr style={{ borderTop: '2px solid var(--brand-navy)' }}>
                      <td></td>
                      <td style={{ padding: '8px 8px', fontWeight: 700, color: 'var(--brand-navy)' }}>Gesamt ({mailStats.total_users})</td>
                      <td style={{ textAlign: 'right', padding: '8px 8px', fontWeight: 700, color: 'var(--brand-navy)' }}>{summary.total_inbox}</td>
                      <td style={{ textAlign: 'right', padding: '8px 8px', fontWeight: 700, color: 'var(--brand-text-muted)' }}>{summary.total_unread}</td>
                      <td></td>
                      <td style={{ textAlign: 'right', padding: '8px 8px', fontWeight: 700, color: 'var(--brand-navy)' }}>{summary.total_received_today}</td>
                      {!isCustomDate && <td style={{ textAlign: 'right', padding: '8px 8px', fontWeight: 700, color: 'var(--brand-text-muted)' }}>{summary.total_received_7d}</td>}
                      <td style={{ textAlign: 'right', padding: '8px 8px', fontWeight: 700, color: 'var(--brand-navy)' }}>{summary.total_sent_today}</td>
                      {!isCustomDate && <td style={{ textAlign: 'right', padding: '8px 8px', fontWeight: 700, color: 'var(--brand-text-muted)' }}>{summary.total_sent_7d}</td>}
                      <td style={{ textAlign: 'right', padding: '8px 8px', fontWeight: 700, color: 'var(--brand-text-muted)' }}>{summary.total_drafts}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>
          </>
        ) : (
          <div style={{ ...card, color: 'var(--brand-text-muted)', fontSize: 13, padding: '1rem' }}>Keine Benutzer gefunden.</div>
        )}
      </div>

      {/* Modals */}
      {inboxModal && (
        <InboxModal
          email={inboxModal.email}
          name={inboxModal.name}
          token={token}
          initialFilter={inboxModal.filter || 'all'}
          onClose={() => setInboxModal(null)}
        />
      )}
      {digestModal && (
        <DigestSettingsModal
          token={token}
          onClose={() => setDigestModal(false)}
        />
      )}
      {criticalOverview && mailStats?.users && (
        <CriticalOverview
          criticalEmails={
            mailStats.users
              .filter(u => u.priority_level !== 'ok')
              .map(u => u.email)
          }
          token={token}
          onOpenInbox={(data) => setInboxModal({ email: data.email, name: data.name, filter: data.filter || 'critical' })}
          onClose={() => setCriticalOverview(false)}
        />
      )}
    </div>
  );
}
